import React, { useState } from 'react';
import { 
  Eye, 
  EyeOff, 
  Type, 
  Minus, 
  Plus, 
  RotateCcw, 
  Focus,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';

const AccessibilityToolbar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  const {
    isHighContrast,
    isReducedMotion,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    canIncreaseFontSize,
    canDecreaseFontSize,
    announce
  } = useAccessibility();

  const handleFontSizeChange = (action) => {
    if (action === 'increase') {
      increaseFontSize();
      announce('Font size increased');
    } else if (action === 'decrease') {
      decreaseFontSize();
      announce('Font size decreased');
    } else {
      resetFontSize();
      announce('Font size reset to default');
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    announce(isMuted ? 'Sound enabled' : 'Sound muted');
  };

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {/* Main Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        title="Accessibility Options"
        aria-label="Toggle accessibility options"
      >
        <Eye className="h-5 w-5" />
      </button>

      {/* Expanded Toolbar */}
      {isExpanded && (
        <div className="absolute bottom-16 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[280px]">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Accessibility Options
          </h3>

          <div className="space-y-4">
            {/* Font Size Controls */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Font Size
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleFontSizeChange('decrease')}
                  disabled={!canDecreaseFontSize}
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Decrease font size"
                >
                  <Minus className="h-4 w-4" />
                </button>
                
                <span className="px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white rounded text-sm font-medium min-w-[80px] text-center">
                  {fontSize}
                </span>
                
                <button
                  onClick={() => handleFontSizeChange('increase')}
                  disabled={!canIncreaseFontSize}
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Increase font size"
                >
                  <Plus className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => handleFontSizeChange('reset')}
                  className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                  title="Reset font size"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* High Contrast Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                High Contrast
              </label>
              <button
                onClick={() => {
                  // Toggle high contrast (this would need to be implemented in the hook)
                  announce('High contrast toggled');
                }}
                className={`p-2 rounded ${
                  isHighContrast
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
                title="Toggle high contrast"
              >
                {isHighContrast ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            </div>

            {/* Reduced Motion Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Reduced Motion
              </label>
              <button
                onClick={() => {
                  // Toggle reduced motion (this would need to be implemented in the hook)
                  announce('Reduced motion toggled');
                }}
                className={`p-2 rounded ${
                  isReducedMotion
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
                title="Toggle reduced motion"
              >
                <Focus className="h-4 w-4" />
              </button>
            </div>

            {/* Sound Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Sound
              </label>
              <button
                onClick={handleMuteToggle}
                className={`p-2 rounded ${
                  !isMuted
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
                title="Toggle sound"
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            </div>

            {/* Keyboard Navigation Help */}
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Keyboard Shortcuts
              </h4>
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div>Tab - Navigate</div>
                <div>Enter/Space - Activate</div>
                <div>Escape - Close</div>
                <div>Ctrl+Shift+P - Performance</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityToolbar;
